<template>
  <div class="body">
  <h1>Glossary</h1>


  <p align="justify">Non-exhaustive list of definitions of terms used in <router-link to="/gerontology">gerontology</router-link> and in <strong>senescence.info</strong>.</p></blockquote>

  <hr width="80%" size="1">


    <p align="justify"><b>Aging:</b> a progressive deterioration of physiological function, an intrinsic age-related process of loss of viability and increase in vulnerability. In humans, aging is characterized by a <router-link to="/ageing-definition">complex phenotype</router-link>.</p>

    <p align="justify"><b>Allele:</b> one of two or more variant forms of a gene.</p>

    <p align="justify"><b>Aphagy:</b> inability to feed oneself due to anatomical deficiencies, common in the adult phase of some species of <router-link to="/ageing-animals">animals</router-link>.</p>

    <p align="justify"><b>Apoptosis:</b> programmed cell death.</p>

    <p align="justify"><b>Antagonistic pleiotropy:</b> <router-link to="/evolution-of-ageing">theory</router-link> by George Williams that explains the existence of aging by the existence of genes beneficial early in life but harmful at later stages.</p>

    <p align="justify"><b>Autophagy:</b> digestion of the cell's own components; it has been <router-link to="/causes-of-ageing">implicated in aging</router-link>.</p>

    <p align="justify"><b>Biogerontology:</b> the scientific study of the biological process of aging.</p>

    <p align="justify"><b><router-link to="/caloric-restriction">Caloric restriction</router-link> (CR):</b> diet regime consisting of eating considerably fewer calories, without malnutrition, that has been considered as a potential method to delay aging.</p>

    <p align="justify"><b>Cellular or clonal senescence:</b> see replicative senescence.</p>

    <p align="justify"><b>Cellular immortality:</b> the ability of certain cell populations, like most cancer cells, to divide indefinitely in culture.</p>

    <p align="justify"><b>Demography:</b> the statistical study of populations; human aging is characterized by <router-link to="/ageing-definition">demographic aging</router-link>.</p>

    <p align="justify"><b>Developmental theory of aging (DevAge):</b> <router-link to="/programmed-ageing">theory</router-link> arguing that aging is an extension of developmental mechanisms.</p>

    <p align="justify"><b>Diphyodont:</b> an animal that develops two successive sets of teeth, common in <router-link to="/ageing-animals">most mammals</router-link>.</p>

    <p align="justify"><b>Diploid:</b> a cell with two sets of chromosomes.</p>

    <p align="justify"><b>Disposable soma theory:</b> <router-link to="/evolution-of-ageing">theory</router-link> by Thomas Kirkwood that explains the existence of aging by the allocation of resources from somatic maintenance to reproduction.</p>

    <p align="justify"><b>DNA damage theory of aging:</b> <router-link to="/causes-of-ageing">theory</router-link> that argues that aging is due to the accumulation of DNA damage with ensuing cellular alterations and disruption of tissue homeostasis.</p>

    <p align="justify"><b>Dyskeratosis congenita:</b> genetic disease caused by defects in the dyskerin protein, one of the components of <router-link to="/telomeres-telomerase">telomerase</router-link>.</p>

    <p align="justify"><b>Ectotherm:</b> a cold-blooded animal, such as a reptile, fish, or amphibian, whose body temperature is mostly determined by the surrounding environment.</p>

    <p align="justify"><b>Endocrine system:</b> group of hormone-producing glands and their secretions (hormones); the endocrine system have been <router-link to="/programmed-ageing">implicated in aging</router-link>.</p>

    <p align="justify"><b>Endotherm:</b> a warm-blooded animal, like a bird and mammal, capable of regulating its internal temperature.</p>

    <p align="justify"><b>Epigenetics:</b> study of heritable changes in a phenotype that are not due to alterations in the DNA sequence but rather due to chemical changes of the DNA and associated proteins.</p>

    <p align="justify"><b>Eutherian:</b> a placental mammal. All mammals are eutherians with the exception of marsupials and monotremes.</p>

    <p align="justify"><b>Exonuclease:</b> enzyme that cleaves nucleotides from one end of a strand of nucleic acid.</p>

    <p align="justify"><b>Free radical theory of aging:</b> <router-link to="/causes-of-ageing">theory</router-link> by Denham Harman that argues that aging is a result of damage accumulation caused by reactive oxygen species.</p>

    <p align="justify"><b>Gene:</b> DNA sequence that encodes a protein and represents the basic unit of inheritance.</p>
    <p align="justify"><b>Genetics:</b> the study of heredity--i.e., the passing of characteristics from one generation to another--and of variation of inherited characteristics. Aging has a strong <router-link to="/genetics-of-ageing">genetic component</router-link>.</p>

    <p align="justify"><b>Genomics:</b> the study of an organism's genome.</p>

    <p align="justify"><b>Genome:</b> the full DNA sequence of an organism.</p>

    <p align="justify"><b>Genotype:</b> genetic makeup of a given organism, usually related to a given characteristic.</p>

    <p align="justify"><b>Geriatrics:</b> the medical study of diseases and problems of the elderly.</p>

    <p align="justify"><b>Germ cells:</b> the reproductive cells which contain the genetic material passed on to the offspring.</p>

    <p align="justify"><b><router-link to="/gerontology">Gerontology</router-link>:</b> the scientific study of the aging process and old age. In the context of <strong>senescence.info</strong>, gerontology refers to the biological study of aging and old age, also called biogerontology.</p>

    <p align="justify"><b><router-link to="/cell-ageing">Hayflick limit</router-link>:</b> the inability of cells to replicative indefinitely in culture.</p>

    <p align="justify"><b>Helicase:</b> an enzyme that unwinds the DNA helix.</p>

    <p align="justify"><b>IMR:</b> initial mortality rate. The age-independent mortality rate obtained from the <router-link to="/ageing-definition">Gompertz equation</router-link>.</p>

    <p align="justify"><b>Iteroparous:</b> an organism that may reproduce more than once during its lifespan.</p>

    <p align="justify"><b>Life expectancy:</b> how long, on average, an animal can be expected to live. Can be used interchangeably with average lifespan and average longevity.</p>

    <p align="justify"><b>Life history:</b> the changes organisms undergo from conception to death, focusing particularly on the schedule of reproduction and survival.</p>

    <p align="justify"><b>Lifespan:</b> the period of time in which the life events of a species or sub-species (e.g., a strain or population) typically occur. Can sometimes be used interchangeably with longevity even though they have slightly different meanings.</p>

    <p align="justify"><b>Longevity:</b> the period of time an organism is expected to live under ideal circumstances. Can sometimes be used interchangeably with lifespan even though they have slightly different meanings.</p>

    <p align="justify"><b>Maximum lifespan (<em>tmax</em>):</b> the maximum period of time organisms of a given species or sub-species (e.g., a strain or population) can live. Usually refers to the longevity of the longest-lived individual of a given species or sub-species.</p>
    <p align="justify"><b>Mechanical senescence:</b> age-related changes that are a consequence of mechanical usage.</p>

    <p align="justify"><b>Mitochondrion:</b> cellular organelle that produces most of the cell's energy.</p>

    <p align="justify"><b>MRDT:</b> mortality rate doubling time. The time required for the mortality rate to double. Inferred from the <router-link to="/ageing-definition">Gompertz equation</router-link>.</p>

    <p align="justify"><b>Mutation:</b> change in the DNA sequence of an organism or cell.</p>
    <p align="justify"><b>Mutation accumulation theory:</b> <router-link to="/evolution-of-ageing">theory</router-link> by Peter Medawar that explains the existence of aging by the accumulation of mutations with harmful effects at later ages.</p>

    <p align="justify"><b>Negligible senescence:</b> organisms in which the aging process has not been detected in spite of detailed studies, as observed in <router-link to="/ageing-animals">some animals</router-link>.</p>

    <p align="justify"><b>Oocyte:</b> a female gametocyte that develops into an ovum after two meiotic divisions.</p>
    <p align="justify"><b>Oogenesis:</b> formation of new oocytes.</p>
    <p align="justify"><b>Oxidative stress:</b> damage caused by reactive oxygen species; oxidative stress has been <router-link to="/causes-of-ageing">implicated in aging</router-link>.</p>
    <p align="justify"><b>Phenotype:</b> the characteristics of an organism as determined by both genetic makeup and environmental influences.</p>

    <p align="justify"><b>Phylogeny:</b> the evolutionary development and history of a species or taxonomic group of species.</p>

    <p align="justify"><b>Polyphenism:</b> the ability of a single genome to give rise to two or more morphologies.</p>

    <p align="justify"><b>Polyphyodont:</b> an animal that develops several sets of teeth successively throughout its life, as observed in <router-link to="/ageing-animals">many species</router-link>.</p>

    <p align="justify"><b>Polyploid:</b> a cell with three or more sets of chromosomes.</p>

    <p align="justify"><b>Progeria:</b> genetic disease resembling accelerated aging which typically affects children. Also called Hutchinson-Gilford syndrome.</p>

    <p align="justify"><b>Progeroid:</b> a phenotype with features resembling accelerated aging.</p>

    <p align="justify"><b>Quiescent:</b> in cell biology, a quiescent cell is one that is not dividing.</p>

    <p align="justify"><b>Rate of living theory:</b> <router-link to="/causes-of-ageing">theory</router-link> that argues that lifespan inversely correlates with metabolic rates.</p>

    <p align="justify"><b>Reactive oxygen species (ROS):</b> any of a number of highly reactive forms of oxygen that are potential sources of damage; damage caused by ROS has been <router-link to="/causes-of-ageing">implicated in aging</router-link>.</p>
    <p align="justify"><b><router-link to="/cell-ageing">Replicative senescence</router-link>:</b> irreversible cessation of cell division of normally proliferating cells. It is also characterized by <router-link to="/cell-ageing">various biomarkers</router-link> and can or not be accompanied by cell death.</p>

    <p align="justify"><b>Semelparous:</b> organisms that reproduce only once, usually followed by death, as observed in <router-link to="/ageing-animals">several animals</router-link>.</p>
    <p align="justify"><b>Senescence:</b> the fundamental process of aging or aging itself. Can also refer to <router-link to="/cell-ageing">cellular aging</router-link> in some contexts.</p>
    <p align="justify"><b>Senescent cell:</b> normally dividing cell that is irreversibly growth arrested and exhibits a number of other biomarkers associated with <router-link to="/cell-ageing">cellular senescence</router-link>.</p>
    <p align="justify"><b>Soma:</b> the entire body of an organism with exception of the germ cells.</p>
    <p align="justify"><b>Stem cell:</b> an undifferentiated cell that can divide, differentiate into specialized cells, and can self-renew to give rise to more stem cells.</p>

    <p align="justify"><b><router-link to="/sens">Strategies for engineered negligible senescence</router-link> (SENS):</b> a proposal by Aubrey de Grey that details how by reversing seven forms of cellular and molecular age-related changes will allow us to cure aging.</p>

    <p align="justify"><b>Stress-induced premature senescence (SIPS):</b> irreversible  cell cycle arrest and associated <router-link to="/cell-ageing">cell phenotypes</router-link> as the result of subcytotoxic stress.</p>

    <p align="justify"><b>Supercentenarian:</b> someone 110 years of age or older.</p>

    <p align="justify"><b>Topoisomerase:</b> an enzyme that regulate the supercoiling structure of the DNA.</p>

    <p align="justify"><b>Taxon (plural: taxa):</b> a taxonomic group of any rank.</p>

    <p align="justify"><b><router-link to="/telomeres-telomerase">Telomeres</router-link>:</b> the long end sequences of a DNA strand occurring at the tip of the chromosomes that play a key role in <router-link to="/cell-ageing">replicative senescence</router-link>.</p>

    <p align="justify"><b><router-link to="/telomeres-telomerase">Telomerase</router-link>:</b> enzyme that adds telomeric sequences to the telomeres and has been associated with cellular immortality.</p>

    <p align="justify"><b>Trait:</b> a particular characteristic of an organism that can have different phenotypes.</p>

    <p align="justify"><b>Werner syndrome (WS):</b> genetic disease resembling accelerated aging; it typically has an adult onset.</p>

  </div>

</template>
<script>
import Footer from "./AppFooter";
export default {
  components: {Footer},
  methods: {
    goto(refName) {
      var element = this.$refs[refName];
      var top = element.offsetTop;

      window.scrollTo(0, top);
    }
  }
};
</script>
<style>
h2 {
  padding-top: 30px;
}
.body {
  margin-left: 10px;
  margin-right: 10px;
}
.sidebar {
  position: -webkit-sticky;
  position: sticky;
  top: 3%;
}

</style>



// WEBPACK FOOTER //
// src/components/Glossary.vue